import React, { useState, useEffect } from 'react';
import {
  Package,
  FileText,
  Mic,
  MessageSquare
} from 'lucide-react';
import MainLayout from '../layouts/MainLayout';
import * as productService from '../services/productService';
import * as blogService from '../services/blogService';
import * as contactService from '../services/contactService';
import * as resourcesService from '../services/resourcesService';

const Dashboard: React.FC = () => {
  const [dashboardStats, setDashboardStats] = useState({
    productCount: 0,
    blogCount: 0,
    podcastCount: 0,
    rfqCount: 0,
    resourceCount: 0
  });
  const [recentRFQs, setRecentRFQs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Dashboard verilerini API'den yükle
  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Parallel olarak tüm verileri yükle
      const [products, blogs, contacts, resources] = await Promise.all([
        productService.getProducts().catch(() => []),
        blogService.getBlogs().catch(() => []),
        contactService.getContacts().catch(() => []),
        resourcesService.getResources().catch(() => [])
      ]);

      // Podcast'leri blog'lardan filtrele
      const podcasts = blogs.filter((blog: any) => 
        blog.tags && blog.tags.includes('podcast')
      );

      // Blog'ları filtrele (podcast olmayanlar)
      const actualBlogs = blogs.filter((blog: any) => 
        !blog.tags || !blog.tags.includes('podcast')
      );

      setDashboardStats({
        productCount: products.length,
        blogCount: actualBlogs.length,
        podcastCount: podcasts.length,
        rfqCount: contacts.length,
        resourceCount: resources.length
      });

      // Son 5 RFQ'yu al
      setRecentRFQs(contacts.slice(0, 5));

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Fallback values
      setDashboardStats({
        productCount: 0,
        blogCount: 0,
        podcastCount: 0,
        rfqCount: 0,
        resourceCount: 0
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const stats = [
    {
      title: 'Toplam Ürün',
      value: dashboardStats.productCount,
      icon: Package,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Blog Yazısı',
      value: dashboardStats.blogCount,
      icon: FileText,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Podcast',
      value: dashboardStats.podcastCount,
      icon: Mic,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      title: 'RFQ Talepleri',
      value: dashboardStats.rfqCount,
      icon: MessageSquare,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'new':
        return 'Yeni';
      case 'in-progress':
        return 'İşlemde';
      case 'resolved':
        return 'Çözüldü';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  return (
    <MainLayout title="Dashboard">
      <div className="space-y-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="bg-white rounded-lg shadow-card p-6 card-hover">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      {stat.title}
                    </p>
                    <p className="text-3xl font-bold text-gray-900">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-full ${stat.bgColor}`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent RFQ Requests */}
        <div className="bg-white rounded-lg shadow-card">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Son RFQ Talepleri
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Müşteri
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Şirket
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarih
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                      Yükleniyor...
                    </td>
                  </tr>
                ) : recentRFQs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                      Henüz RFQ talebi yok
                    </td>
                  </tr>
                ) : recentRFQs.map((rfq) => (
                  <tr key={rfq.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {rfq.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {rfq.company}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {rfq.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(rfq.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(rfq.status)}`}>
                        {getStatusText(rfq.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;

